// src/components/admin/BatchCodeGenerator.tsx
import React, { useState } from 'react';
import { Calendar, AlertCircle, Check, Download, Clipboard, Tag, Copy, X } from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import { PromoCode } from '../../types/admin';

interface BatchCodeGeneratorProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  count: number;
  prefix: string;
  planType: 'medium' | 'premium';
  durationType: 'once' | 'repeating' | 'forever';
  durationMonths: number | null;
  expiresAt: string | null;
  maxRedemptions: number | null;
  discountPercentage: number | null;
  description: string;
}

const BatchCodeGenerator: React.FC<BatchCodeGeneratorProps> = ({
  onSuccess,
  onCancel
}) => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    count: 10,
    prefix: '',
    planType: 'medium',
    durationType: 'once',
    durationMonths: 1,
    expiresAt: null,
    maxRedemptions: 1,
    discountPercentage: null,
    description: ''
  });
  
  // Form UI state
  const [usePrefix, setUsePrefix] = useState(false);
  const [useExpiration, setUseExpiration] = useState(false);
  const [useMaxRedemptions, setUseMaxRedemptions] = useState(true);
  const [useDiscount, setUseDiscount] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Generated codes
  const [generatedCodes, setGeneratedCodes] = useState<PromoCode[]>([]);
  
  // Success message
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Clipboard state
  const [codeCopied, setCodeCopied] = useState(false);
  
  // Form validation
  const validate = (): boolean => {
    // Clear previous errors
    setError(null);
    
    // Validate count
    if (!formData.count || formData.count < 1 || formData.count > 100) {
      setError('Please enter a valid count between 1 and 100');
      return false;
    }
    
    // Validate prefix if enabled
    if (usePrefix && !formData.prefix.trim()) {
      setError('Please enter a prefix or disable the prefix option');
      return false;
    }
    
    // Validate plan type
    if (!['medium', 'premium'].includes(formData.planType)) {
      setError('Please select a valid plan type');
      return false;
    }
    
    // Validate duration type
    if (!['once', 'repeating', 'forever'].includes(formData.durationType)) {
      setError('Please select a valid duration type');
      return false;
    }
    
    // Duration months is required for once and repeating
    if (['once', 'repeating'].includes(formData.durationType) && !formData.durationMonths) {
      setError('Duration months is required for one-time or recurring codes');
      return false;
    }
    
    // Validate expiration date if enabled
    if (useExpiration && !formData.expiresAt) {
      setError('Expiration date is required when enabled');
      return false;
    }
    
    // Validate max redemptions if enabled
    if (useMaxRedemptions && 
        (formData.maxRedemptions === null || formData.maxRedemptions <= 0)) {
      setError('Maximum redemptions must be a positive number');
      return false;
    }
    
    // Validate discount percentage if enabled
    if (useDiscount && 
        (formData.discountPercentage === null || 
         formData.discountPercentage <= 0 || 
         formData.discountPercentage > 100)) {
      setError('Discount percentage must be between 1 and 100');
      return false;
    }
    
    return true;
  };
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Handle numeric inputs
    if (type === 'number') {
      setFormData({
        ...formData,
        [name]: value ? Number(value) : null
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validate()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Prepare data for API
      const apiData = {
        ...formData,
        // Include optional fields only if enabled
        prefix: usePrefix ? formData.prefix : null,
        expiresAt: useExpiration ? formData.expiresAt : null,
        maxRedemptions: useMaxRedemptions ? formData.maxRedemptions : null,
        discountPercentage: useDiscount ? formData.discountPercentage : null
      };
      
      // Submit to API
      const codes = await adminApi.generateBatchPromoCodes(apiData);
      
      // Show generated codes
      setGeneratedCodes(codes);
      setShowSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate promo codes');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle exporting codes to CSV
  const handleExportCSV = () => {
    if (!generatedCodes.length) return;
    
    // Create CSV content
    let csv = 'Code,Plan Type,Duration Type,Duration Months,Expires At,Max Redemptions,Discount Percentage,Is Active,Created At\n';
    
    generatedCodes.forEach(code => {
      csv += [
        code.code,
        code.planType,
        code.durationType,
        code.durationMonths || '',
        code.expiresAt || '',
        code.maxRedemptions || '',
        code.discountPercentage || '',
        'true',
        new Date().toISOString()
      ].join(',') + '\n';
    });
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promo-codes-batch-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Handle copying all codes
  const handleCopyAll = () => {
    if (!generatedCodes.length) return;
    
    const codeText = generatedCodes.map(code => code.code).join('\n');
    
    navigator.clipboard.writeText(codeText).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };
  
  // Render form or results
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Batch Generate Promo Codes</h2>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Cancel
        </button>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}
      
      {showSuccess && generatedCodes.length > 0 ? (
        // Success screen with generated codes
        <div>
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6 flex items-start">
            <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-green-700 font-medium">
                Successfully generated {generatedCodes.length} promo codes!
              </p>
              <p className="text-green-600 text-sm mt-1">
                You can copy the codes below or export them as a CSV file.
              </p>
            </div>
          </div>
          
          <div className="bg-white border rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Generated Codes</h3>
              <div className="flex space-x-2">
                <button
                  onClick={handleCopyAll}
                  className="flex items-center px-3 py-1.5 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  title="Copy all codes"
                >
                  {codeCopied ? 
                    <><Check className="h-4 w-4 mr-1.5" /> Copied!</> : 
                    <><Copy className="h-4 w-4 mr-1.5" /> Copy All</>
                  }
                </button>
                <button
                  onClick={handleExportCSV}
                  className="flex items-center px-3 py-1.5 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  title="Export as CSV"
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  Export CSV
                </button>
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto border rounded-md p-2 bg-gray-50 font-mono">
              {generatedCodes.map((code, index) => (
                <div 
                  key={index} 
                  className="flex justify-between items-center py-2 px-3 hover:bg-gray-100 rounded"
                >
                  <span>{code.code}</span>
                  <div className="text-xs text-gray-500">
                    {code.planType} | {code.durationType}
                    {code.durationType !== 'forever' && code.durationMonths ? 
                      ` (${code.durationMonths} months)` : 
                      ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between">
            <button
              onClick={() => {
                setGeneratedCodes([]);
                setShowSuccess(false);
              }}
              className="px-4 py-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50"
            >
              Generate New Batch
            </button>
            <button
              onClick={onSuccess}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        // Batch generation form
        <form onSubmit={handleSubmit}>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <div className="flex items-start">
              <Tag className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-blue-700 text-sm">
                Create multiple promo codes at once with the same settings. You can generate up to 100 codes in a single batch.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Batch Count */}
            <div>
              <label htmlFor="count" className="block text-sm font-medium text-gray-700 mb-1">
                Number of Codes to Generate
              </label>
              <input
                type="number"
                id="count"
                name="count"
                value={formData.count}
                onChange={handleChange}
                min="1"
                max="100"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum: 100 codes per batch
              </p>
            </div>
            
            {/* Prefix Option */}
            <div>
              <div className="flex items-center mb-1">
                <input
                  type="checkbox"
                  id="usePrefix"
                  checked={usePrefix}
                  onChange={() => setUsePrefix(!usePrefix)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="usePrefix" className="ml-2 block text-sm font-medium text-gray-700">
                  Use Prefix (e.g., SUMMER-, BLACK-FRIDAY-)
                </label>
              </div>
              {usePrefix && (
                <input
                  type="text"
                  id="prefix"
                  name="prefix"
                  value={formData.prefix}
                  onChange={handleChange}
                  placeholder="Enter prefix (e.g., SUMMER-)"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              )}
            </div>
            
            {/* Plan Type */}
            <div>
              <label htmlFor="planType" className="block text-sm font-medium text-gray-700 mb-1">
                Plan Type
              </label>
              <select
                id="planType"
                name="planType"
                value={formData.planType}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="medium">Medium</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            
            {/* Duration Type */}
            <div>
              <label htmlFor="durationType" className="block text-sm font-medium text-gray-700 mb-1">
                Duration Type
              </label>
              <select
                id="durationType"
                name="durationType"
                value={formData.durationType}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="once">One-time</option>
                <option value="repeating">Recurring</option>
                <option value="forever">Permanent</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {formData.durationType === 'once' && 'One-time: Valid for a specific duration, then expires.'}
                {formData.durationType === 'repeating' && 'Recurring: Auto-renews for the specified duration.'}
                {formData.durationType === 'forever' && 'Permanent: Never expires until manually deactivated.'}
              </p>
            </div>
            
            {/* Duration Months - Only for once and repeating */}
            {formData.durationType !== 'forever' && (
              <div>
                <label htmlFor="durationMonths" className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (Months)
                </label>
                <input
                  type="number"
                  id="durationMonths"
                  name="durationMonths"
                  value={formData.durationMonths || ''}
                  onChange={handleChange}
                  min="1"
                  max="60"
                  placeholder="Enter duration in months"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            )}
            
            {/* Expiration Date - Optional */}
            <div>
              <div className="flex items-center mb-1">
                <input
                  type="checkbox"
                  id="useExpiration"
                  checked={useExpiration}
                  onChange={() => setUseExpiration(!useExpiration)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="useExpiration" className="ml-2 block text-sm font-medium text-gray-700">
                  Set Expiration Date
                </label>
              </div>
              {useExpiration && (
                <div className="flex items-center">
                  <input
                    type="date"
                    id="expiresAt"
                    name="expiresAt"
                    value={formData.expiresAt || ''}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]} // Min date is today
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <Calendar className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" />
                </div>
              )}
            </div>
            
            {/* Max Redemptions */}
            <div>
              <div className="flex items-center mb-1">
                <input
                  type="checkbox"
                  id="useMaxRedemptions"
                  checked={useMaxRedemptions}
                  onChange={() => setUseMaxRedemptions(!useMaxRedemptions)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="useMaxRedemptions" className="ml-2 block text-sm font-medium text-gray-700">
                  Set Maximum Redemptions
                </label>
              </div>
              {useMaxRedemptions && (
                <input
                  type="number"
                  id="maxRedemptions"
                  name="maxRedemptions"
                  value={formData.maxRedemptions || ''}
                  onChange={handleChange}
                  min="1"
                  placeholder="Max number of times each code can be used"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              )}
            </div>
            
            {/* Discount Percentage - Optional */}
            <div>
              <div className="flex items-center mb-1">
                <input
                  type="checkbox"
                  id="useDiscount"
                  checked={useDiscount}
                  onChange={() => setUseDiscount(!useDiscount)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="useDiscount" className="ml-2 block text-sm font-medium text-gray-700">
                  Set Discount Percentage (for reference)
                </label>
              </div>
              {useDiscount && (
                <div className="relative mt-1 rounded-md shadow-sm">
                  <input
                    type="number"
                    id="discountPercentage"
                    name="discountPercentage"
                    value={formData.discountPercentage || ''}
                    onChange={handleChange}
                    min="1"
                    max="100"
                    placeholder="Discount percentage"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 pr-12"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Description Field - Full width */}
            <div className="col-span-1 md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Batch Description (Internal Use)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Enter a description for this batch of promo codes (for admin reference only)"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 mr-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Generating...' : 'Generate Codes'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default BatchCodeGenerator;